import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { useT } from '../lib/i18n';

interface ConfirmOptions {
  title?: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'default';
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return context;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useT();
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        options,
        resolve,
      });
    });
  }, []);

  const handleConfirm = () => {
    if (confirmState) {
      confirmState.resolve(true);
      setConfirmState(null);
    }
  };

  const handleCancel = () => {
    if (confirmState) {
      confirmState.resolve(false);
      setConfirmState(null);
    }
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      
      {/* DaisyUI Modal */}
      {confirmState?.isOpen && (
        <div className="modal modal-open z-[9999]" onClick={handleCancel}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15 }}
            className="modal-box relative card max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Icon */}
            <div className="flex flex-col items-center text-center gap-4 pt-2 pb-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                confirmState.options.type === 'danger'
                  ? 'bg-red-500/15 text-red-500'
                  : 'bg-primary-500/15 text-primary-500'
              }`}>
                <AlertTriangle className="w-7 h-7" />
              </div>
              <h3 className="font-bold text-xl">
                {confirmState.options.title || t('common.messages.confirmAction')}
              </h3>
            </div>

            {/* Content */}
            <div className="text-center text-dark-muted whitespace-pre-line leading-relaxed px-2">
              {confirmState.options.message}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 mt-6">
              <button
                onClick={handleConfirm}
                className={`btn w-full ${
                  confirmState.options.type === 'danger'
                    ? 'btn-danger'
                    : 'btn-primary'
                }`}
              >
                {confirmState.options.confirmText || t('common.buttons.confirm')}
              </button>
              <button
                onClick={handleCancel}
                className="btn btn-ghost w-full"
              >
                {confirmState.options.cancelText || t('common.buttons.cancel')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}


